import { GetProductsRequest, GetProductsResponse, SearchItem } from '@yiluhub/yilu-amp-types';
import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { yiluEnv } from 'utils';
import { getCurrentLanguage } from 'utils/i18n';

import { PassType } from 'modules/eurail/utils/types';

export type UseEurailSearchResultsLoaderParams = {
  searchParams: {
    fareClass: string;
    adult: number;
    youth: number;
    child: number;
    senior: number;
    pass: PassType;
    to?: string;
    currency?: string;
  };
  onSearchResults?: (results: SearchItem[]) => unknown;
  onNoSearchResults?: () => unknown;
  onError?: (error: Error) => unknown;
  pageSize?: number;
};

export const useEurailSearchResultsLoader = ({
  searchParams,
  onSearchResults,
  onError,
}: UseEurailSearchResultsLoaderParams) => {
  const [searchResults, setSearchResults] = useState<SearchItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const lastParamsRef = useRef<typeof searchParams>();
  const {
    YILU_AMP_STORE_ID,
    VITE_YILU_AMP_EURAIL_VERTICAL_ID,
    YILU_AMP_BACKEND_URL,
    YILU_AMP_EURAIL_COUNTRY_PASS_CATALOG_ID,
    YILU_AMP_EURAIL_GLOBAL_PASS_CATALOG_ID,
  } = yiluEnv.getVariables();

  const fetchEurailTickets = useCallback(async () => {
    const eurailRequest: GetProductsRequest['query'] = {
      storeId: YILU_AMP_STORE_ID,
      verticalId: VITE_YILU_AMP_EURAIL_VERTICAL_ID,
      catalogId:
        searchParams.pass === PassType.ONE_COUNTRY
          ? YILU_AMP_EURAIL_COUNTRY_PASS_CATALOG_ID
          : YILU_AMP_EURAIL_GLOBAL_PASS_CATALOG_ID,
      fareClass: Number(searchParams?.fareClass) || 2,
      adults: searchParams.adult,
      youth: searchParams.youth,
      infants: searchParams.child,
      seniors: searchParams.senior,
      to: PassType.ONE_COUNTRY ? searchParams.to : undefined,
      pageNumber: 0,
      pageSize: 10,
      locale: getCurrentLanguage(),
      currency: searchParams.currency ?? 'EUR',
    };

    const response = await axios.get<GetProductsResponse>(
      `${YILU_AMP_BACKEND_URL}/product/v1/products`,
      { params: eurailRequest },
    );

    const body = response.data;
    const newSearchResults = body.items;

    onSearchResults?.(newSearchResults);
    setSearchResults([...newSearchResults]);
  }, [
    YILU_AMP_EURAIL_COUNTRY_PASS_CATALOG_ID,
    YILU_AMP_EURAIL_GLOBAL_PASS_CATALOG_ID,
    YILU_AMP_STORE_ID,
    VITE_YILU_AMP_EURAIL_VERTICAL_ID,
    YILU_AMP_BACKEND_URL,
    searchParams,
    onSearchResults,
  ]);

  const loadResults = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      await fetchEurailTickets();
    } catch (error) {
      console.error('Error loading Eurail search results', error);
      onError?.(error as Error);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, fetchEurailTickets, onError]);

  useEffect(() => {
    const lastParams = lastParamsRef.current;
    const sameParams =
      lastParams &&
      Object.keys(searchParams).every(
        (key) => (searchParams as any)[key] === (lastParams as any)[key],
      );

    if (sameParams) {
      return;
    }

    lastParamsRef.current = searchParams;
    setSearchResults([]);
    void loadResults();
  }, [searchParams, loadResults]);

  return { searchResults, isLoading };
};
